import { useState } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { useEffect, useRef, useCallback } from "react";
import { api } from "../lib/api";
import { useOverlayWS } from "../lib/useOverlayWS";
import { OverlayElement, GroupAnimated } from "../components/OverlayElement";
import { groupAnimOf, hasGroupAnim } from "../lib/groups";
import { FontFaceStyle } from "../components/FontFaceStyle";
import { t } from "../i18n";

export default function Output() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const key = params.get("key");
  const [elements, setElements] = useState([]);
  const [media, setMedia] = useState({});
  const [dims, setDims] = useState({ w: 1920, h: 1080 });
  const [scale, setScale] = useState(1);
  const [error, setError] = useState(null);
  const [ytData, setYtData] = useState(null);
  const [twitchData, setTwitchData] = useState(null);
  const [fonts, setFonts] = useState([]);
  const [sheetValues, setSheetValues] = useState({});
  const [chatMessages, setChatMessages] = useState([]);
  const [chatPinned, setChatPinned] = useState(null);
  const [chatAlert, setChatAlert] = useState(null);
  const triggerKeys = useRef({});
  const [, force] = useState(0);

  // Load state via HTTP (initial render + resync on WS reconnect)
  const loadState = useCallback(() => {
    api
      .get(`/overlays/${id}/public`, { params: { key } })
      .then(({ data }) => {
        setElements(data.overlay.elements || []);
        setMedia(data.media || {});
        setDims({ w: data.overlay.largura, h: data.overlay.altura });
        setFonts(data.fonts || []);
      })
      .catch(() => setError(t("Overlay ou chave inválidos")));
  }, [id, key]);

  useEffect(() => {
    document.body.style.background = "transparent";
    document.documentElement.style.background = "transparent";
    loadState();
  }, [loadState]);

  const computeScale = () => {
    const s = Math.min(window.innerWidth / dims.w, window.innerHeight / dims.h);
    setScale(s > 0 ? s : 1);
  };
  useEffect(() => {
    computeScale();
    window.addEventListener("resize", computeScale);
    return () => window.removeEventListener("resize", computeScale);
  }, [dims]);

  useOverlayWS(id, "output", key, (msg) => {
    if (msg.type === "state") setElements(msg.elements || []);
    else if (msg.type === "update_element") {
      setElements((prev) =>
        prev.map((e) => (e.id === msg.elementId ? { ...e, ...msg.patch } : e))
      );
    } else if (msg.type === "hide_all") {
      setElements((prev) => prev.map((e) => ({ ...e, visivel: false })));
    } else if (msg.type === "trigger") {
      triggerKeys.current[msg.elementId] = (triggerKeys.current[msg.elementId] || 0) + 1;
      force((n) => n + 1);
    } else if (msg.type === "youtube_data") {
      setYtData(msg.data);
    } else if (msg.type === "twitch_data") {
      setTwitchData(msg.data);
    } else if (msg.type === "sheet_values") {
      setSheetValues(msg.values || {});
    } else if (msg.type === "chat_state") {
      setChatMessages(msg.messages || []);
      setChatPinned(msg.pinned || null);
    } else if (msg.type === "chat_messages") {
      setChatMessages((prev) => {
        const ids = new Set(prev.map((x) => x.id));
        const add = (msg.messages || []).filter((m) => !ids.has(m.id));
        return [...prev, ...add].slice(-60);
      });
    } else if (msg.type === "chat_reject") {
      setChatMessages((prev) => prev.filter((x) => x.id !== msg.messageId));
    } else if (msg.type === "chat_pin") {
      setChatPinned(msg.message || null);
    } else if (msg.type === "chat_alert") {
      setChatAlert({ ...msg.message, key: Date.now() });
    }
  }, loadState);

  if (error) {
    return (
      <div style={{ color: "#fff", fontFamily: "monospace", padding: 24 }} data-testid="output-error">
        {error}
      </div>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        overflow: "hidden",
        background: "transparent",
        cursor: "none",
      }}
      data-testid="output-root"
    >
      <FontFaceStyle fonts={fonts} elements={elements} />
      <div
        style={{
          width: dims.w,
          height: dims.h,
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: `translate(-50%, -50%) scale(${scale})`,
          transformOrigin: "center center",
          overflow: "hidden", // o que estiver fora do quadro não aparece na transmissão
        }}
      >
        {(() => {
          const renderEl = (el) => (
            <OverlayElement
              key={el.id}
              el={el}
              media={media}
              triggerKey={triggerKeys.current[el.id] || 0}
              ytData={ytData}
              twitchData={twitchData}
              chatState={{ messages: chatMessages, pinned: chatPinned, alert: chatAlert }}
              sheetValues={sheetValues}
            />
          );
          // Grupos com animação própria são renderizados juntos num invólucro animado.
          const done = new Set();
          // Modo PREVIEW do operador (?pv=id1,id2): só os elementos listados ficam visíveis, independente do que está no ar.
          const pv = params.get("pv");
          const shown = pv != null ? elements.map((x) => ({ ...x, visivel: pv.split(",").includes(x.id) })) : elements;
          return [...shown]
            .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
            .map((el) => {
              if (el.grupo) {
                const gm = shown.filter((x) => x.grupo === el.grupo);
                if (hasGroupAnim(groupAnimOf(gm))) {
                  if (done.has(el.grupo)) return null;
                  done.add(el.grupo);
                  return <GroupAnimated key={`grp-${el.grupo}`} members={gm} renderMember={renderEl} />;
                }
              }
              return renderEl(el);
            });
        })()}
      </div>
    </div>
  );
}
